import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { api } from "./api";
import { useMode } from "./mode";
import { useProfileApi } from "./profile";
import type { Mode } from "./types";

/**
 * Queries shared by several pages, so every page uses the same cache keys.
 * The user is ["me"], and account-level data (not tied to a profile) lives under ["account", ...].
 * Everything about a profile lives under ["profile", profileId, ...] (see `useProfileApi`), with
 * the mode next for test/live data: ["profile", id, mode, <resource>].
 */

export const ME_KEY = ["me"] as const;
/** The user's UnizPay wallet; balances and transfers live under it. */
export const WALLET_KEY = ["account", "wallet"] as const;

export function useMe() {
  return useQuery({ queryKey: ME_KEY, queryFn: api.account.me });
}

export function useWallet() {
  return useQuery({ queryKey: WALLET_KEY, queryFn: api.account.wallet, select: (r) => r.wallet });
}

/** Networks of a mode, before any profile exists (e.g. while creating one). */
export function useAccountNetworks(mode: Mode) {
  return useQuery({
    queryKey: ["account", "networks", mode],
    queryFn: () => api.account.networks(mode),
    staleTime: 5 * 60_000,
  });
}

/** Ends the session and returns to sign-in. */
export function useSignOut() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.auth.logout,
    // Leave even if the request fails: the session is unusable either way.
    onSettled: () => {
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });
}

export function useSettlementSettings() {
  const { api, mode, key } = useProfileApi();
  return useQuery({ queryKey: key("settings"), queryFn: () => api.settings(mode) });
}

export function useNetworks() {
  const { api, mode, key } = useProfileApi();
  return useQuery({ queryKey: key("networks"), queryFn: () => api.networks(mode), staleTime: 5 * 60_000 });
}

export function useCheckoutSettings() {
  const { api, mode, key } = useProfileApi();
  return useQuery({ queryKey: key("checkout-settings"), queryFn: () => api.checkoutSettings(mode) });
}

export function useApiKeys() {
  const { api, mode, key } = useProfileApi();
  return useQuery({ queryKey: key("api-keys"), queryFn: () => api.apiKeys(mode) });
}

export function useWebhookEndpoints() {
  const { api, mode, key } = useProfileApi();
  return useQuery({ queryKey: key("webhook-endpoints"), queryFn: () => api.webhookEndpoints(mode) });
}

export function useMembers() {
  const { api, teamKey } = useProfileApi();
  return useQuery({ queryKey: teamKey("members"), queryFn: api.members });
}

/** The wallet's balances on the current mode's networks (mainnets in live). */
export function useWalletBalances() {
  const { mode } = useMode();
  return useQuery({
    queryKey: [...WALLET_KEY, "balances", mode],
    queryFn: () => api.account.walletBalances(mode),
  });
}

/** The wallet's sends on the current mode's networks. */
export function useWalletTransfers() {
  const { mode } = useMode();
  return useQuery({
    queryKey: [...WALLET_KEY, "transfers", mode],
    queryFn: () => api.account.walletTransfers(mode),
  });
}
